/**
 * Who hosts an event, and what that lets the signed-in operator do (GIS-222).
 *
 * An event has one PRIMARY host, `vendorID`, who created it and is the only
 * person money ever reaches, plus optional co-hosts in `coHostIDs`. Co-hosts
 * run the event with the host (edit it, manage guests and invites, run the
 * queue, message attendees) but never touch pricing, payouts, revenue, coupons,
 * cancelling, restoring or deleting, and only the host or an admin changes who
 * the co-hosts are.
 *
 * Mirrors src/events/shared/eventHosts.js in gist-firebase-functions. The server
 * and firestore.rules are the authority; this decides what the panel offers.
 */

export const MAX_CO_HOSTS = 10;

export const primaryHostID = (event) => {
  const id = event?.vendorID;
  return typeof id === "string" && id ? id : null;
};

export const coHostIDsOf = (event) => {
  const raw = event?.coHostIDs;
  if (!Array.isArray(raw)) return [];
  const primary = primaryHostID(event);
  const out = [];
  raw.forEach((id) => {
    if (typeof id === "string" && id && id !== primary && !out.includes(id)) out.push(id);
  });
  return out;
};

export const eventHostIDs = (event) => {
  const primary = primaryHostID(event);
  return [...(primary ? [primary] : []), ...coHostIDsOf(event)];
};

export const isPrimaryHost = (event, uid) =>
  typeof uid === "string" && !!uid && primaryHostID(event) === uid;

export const isCoHost = (event, uid) =>
  typeof uid === "string" && !!uid && coHostIDsOf(event).includes(uid);

export const isEventHost = (event, uid) => isPrimaryHost(event, uid) || isCoHost(event, uid);

/**
 * A guest list with the hosts on top: the primary host, then co-hosts in the
 * order they were added, then everyone else in the order they came. It only
 * reorders, so a host who isn't on the list is never added.
 */
export const hostsFirst = (event, guests) => {
  const hostIDs = eventHostIDs(event);
  if (hostIDs.length === 0 || !Array.isArray(guests)) return guests;
  const rank = (guest) => {
    const i = hostIDs.indexOf(guest?.userID);
    return i === -1 ? hostIDs.length : i;
  };
  // Array#sort is stable, so guests who aren't hosts keep their order.
  return [...guests].sort((a, b) => rank(a) - rank(b));
};

/**
 * What the operator may do on this event. An admin who also happens to be a
 * co-host keeps admin rights, so `coHostOnly` is the flag the UI restricts on.
 */
export const eventAccessFor = (event, { uid, isSuperAdmin = false } = {}) => {
  const primary = isPrimaryHost(event, uid);
  const coHost = !primary && isCoHost(event, uid);
  const admin = !!isSuperAdmin;
  const ownerOrAdmin = primary || admin;
  return {
    isPrimaryHost: primary,
    isCoHost: coHost,
    coHostOnly: coHost && !admin,
    canSeeEvent: ownerOrAdmin || coHost,
    canChangePricing: ownerOrAdmin,
    canEndEvent: ownerOrAdmin,
    canManageCoHosts: ownerOrAdmin,
  };
};

// The participant arrays that make up the guest list. Mirrors eventGuestIds in
// gist-firebase-functions/src/events/shared/eventAttendees.js.
const GUEST_LIST_FIELDS = ["rsvpSitters", "rsvpMovers", "sitters", "movers"];

/** Everyone who RSVP'd or checked in. People only invited are not guests yet. */
export const guestIDsOf = (event) => {
  const ids = new Set();
  GUEST_LIST_FIELDS.forEach((field) => {
    const list = event?.[field];
    if (!Array.isArray(list)) return;
    list.forEach((id) => {
      if (typeof id === "string" && id) ids.add(id);
    });
  });
  return ids;
};

/**
 * The co-host button for a guest's photo in the guest list, or null for none.
 * Co-hosts are picked from the guest list: the host or an admin gets "make" or
 * "remove" ("full" once the event has MAX_CO_HOSTS co-hosts), and a co-host
 * opening their own photo gets "leave". The primary host's photo never has one.
 */
export const coHostActionFor = (event, guestID, { uid, isSuperAdmin = false } = {}) => {
  if (typeof guestID !== "string" || !guestID || isPrimaryHost(event, guestID)) return null;
  const guestIsCoHost = isCoHost(event, guestID);
  if (eventAccessFor(event, { uid, isSuperAdmin }).canManageCoHosts) {
    if (guestIsCoHost) return "remove";
    if (!guestIDsOf(event).has(guestID)) return null;
    return coHostIDsOf(event).length >= MAX_CO_HOSTS ? "full" : "make";
  }
  return guestIsCoHost && guestID === uid ? "leave" : null;
};

// firestore.rules refuses a co-host write that changes any of these. The edit
// form sends the whole allowlist back on save, so a co-host's save drops them
// rather than relying on each value being unchanged.
export const PRIMARY_ONLY_FIELDS = [
  "cost",
  "priceSchedule",
  "basePrice",
  "firedPriceTierIndices",
  "priceScheduleTasks",
  "isActive",
  "isCompleted",
  "vendorID",
  "coHostIDs",
];

export const withoutPrimaryOnlyFields = (data) => {
  const out = { ...(data || {}) };
  PRIMARY_ONLY_FIELDS.forEach((field) => {
    delete out[field];
  });
  return out;
};

// ---- The events list ------------------------------------------------------

/** Which list tab an event belongs on. Cancelled wins over completed. */
export const tabOfEvent = (event) => {
  if (event?.status === "cancelled") return "cancelled";
  return event?.isCompleted ? "completed" : "upcoming";
};

const dateMillis = (event) => {
  const d = event?.date;
  if (!d) return null;
  if (typeof d.toMillis === "function") return d.toMillis();
  if (typeof d.toDate === "function") return d.toDate().getTime();
  if (d instanceof Date) return d.getTime();
  if (typeof d.seconds === "number") return d.seconds * 1000;
  return null;
};

/** Co-hosted events for one tab, flagged so the rows can show a Co-host badge. */
export const coHostedForTab = (events, tab) =>
  (events || [])
    .filter((event) => tabOfEvent(event) === tab)
    .map((event) => ({ ...event, isCoHosted: true, revenuePending: false }));

export const countCoHostedByTab = (events) => {
  const counts = { upcoming: 0, completed: 0, cancelled: 0 };
  (events || []).forEach((event) => {
    counts[tabOfEvent(event)] += 1;
  });
  return counts;
};

/**
 * The host's own events plus the ones they co-host, one row per event, in the
 * tab's date order: soonest first for upcoming, latest first otherwise.
 */
export const mergeHostEvents = (ownEvents, coHostedEvents, tab) => {
  const own = ownEvents || [];
  const seen = new Set(own.map((event) => event.id));
  const extra = (coHostedEvents || []).filter((event) => !seen.has(event.id));
  if (extra.length === 0) return own;
  const direction = tab === "upcoming" ? 1 : -1;
  return [...own, ...extra].sort((a, b) => {
    const am = dateMillis(a);
    const bm = dateMillis(b);
    if (am === bm) return 0;
    if (am === null) return 1;
    if (bm === null) return -1;
    return (am - bm) * direction;
  });
};
